/**
 * 经典橙色模板 - 列表页
 * 
 * 设计风格（与 Modern 模板完全不同）：
 * - 无侧边栏，全宽度纵向列表布局
 * - 横向卡片：缩略图左侧 + 文字内容右侧（与首页 LatestListings 风格统一）
 * - 水平分类标签栏（移动端横向滚动）
 * - 暖色系，橙色点缀
 * - Hero 图片头图
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const SORT_OPTIONS = [
  { key: "latest", label: "最新发布", icon: "ri-time-line" },
  { key: "popular", label: "浏览最多", icon: "ri-fire-line" },
];

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours === 0) {
      const mins = Math.floor(diff / (1000 * 60));
      return `${mins}分钟前`;
    }
    return `${hours}小时前`;
  }
  if (days === 1) return "昨天";
  if (days < 7) return `${days}天前`;
  if (days < 30) return `${Math.floor(days / 7)}周前`;
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

function ListingCard({ listing, index }: { listing: ListingItem; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const [favorited, setFavorited] = useState(false);

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <Link
        to={`/listing/${listing.id}`}
        className="group flex gap-3 md:gap-4 p-3 md:p-4 bg-white rounded-2xl border border-gray-100 hover:border-orange-200 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
      >
        {/* 缩略图 */}
        <div className="w-28 h-24 md:w-36 md:h-28 shrink-0 rounded-xl overflow-hidden bg-gray-100">
          {listing.images && listing.images[0] ? (
            <img
              src={listing.images[0]}
              alt={listing.title}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <i className="ri-image-line text-2xl" />
            </div>
          )}
        </div>

        {/* 文字内容 */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-sm md:text-base font-semibold text-gray-800 group-hover:text-orange-600 transition-colors line-clamp-1">
                {listing.is_pinned && (
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs rounded mr-1.5 shrink-0 align-middle">
                    置顶
                  </span>
                )}
                {listing.title}
              </h3>
            </div>

            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded-full">
                {listing.category?.name || '未分类'}
              </span>
              <span className="text-gray-400 text-xs">{formatDate(listing.created_at)}</span>
              <span className="text-gray-400 text-xs flex items-center gap-0.5">
                <i className="ri-eye-line" />
                {listing.view_count}
              </span>
            </div>

            <p className="text-gray-500 text-xs md:text-sm mt-2 line-clamp-2 leading-relaxed">
              {listing.content}
            </p>
          </div>

          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
            <div className="flex items-center gap-2">
              <i className="ri-user-line text-xs text-gray-400" />
              <span className="text-xs text-gray-500">{listing.contact_name || '匿名用户'}</span>
            </div>
            <button
              onClick={(e) => { e.preventDefault(); setFavorited(!favorited); }}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-orange-50 transition-colors cursor-pointer"
            >
              <i className={`${favorited ? "ri-heart-fill text-orange-500" : "ri-heart-line text-gray-400"} text-sm`} />
            </button>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function ClassicCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { siteId, loading: siteLoading } = useSite();

  // 数据
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // 筛选
  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);

  const PAGE_SIZE = 10;

  // 加载分类
  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  // 根据 slug 设置当前分类
  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  // 加载列表
  useEffect(() => {
    if (siteLoading || !siteId) return;
    setLoading(true);
    const params: Record<string, any> = { page: currentPage, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, currentPage]);

  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") {
      list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    }
    return list;
  }, [listings, sortBy]);

  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.3 });

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />

      {/* ── 页面头图 Banner ── */}
      <section className="relative bg-gradient-to-br from-orange-100 via-orange-50 to-amber-50 py-12 md:py-16">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 20% 50%, #f97316 0%, transparent 50%), radial-gradient(circle at 80% 50%, #ea580c 0%, transparent 50%)"
        }} />
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 text-center">
          {/* 面包屑 */}
          <nav className="flex items-center justify-center gap-1.5 text-sm text-gray-500 mb-3">
            <Link to="/" className="hover:text-orange-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-gray-700 font-medium">{currentCategoryName}</span>
          </nav>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">
            {currentCategoryName}
          </h1>
          <p className="text-gray-500 text-sm md:text-base">共 {total} 条服务信息</p>
        </div>
      </section>

      {/* ── 分类标签栏 ── */}
      <div className="bg-white border-b border-gray-100 sticky top-[60px] z-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => { setActiveCategoryId(""); setCurrentPage(1); }}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                !activeCategoryId
                  ? "bg-orange-500 text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-orange-600"
              }`}
            >
              <i className="ri-apps-line mr-1" />
              全部
            </button>
            {(navCategories.length > 0 ? navCategories : categories).slice(0, 8).map(cat => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategoryId(cat.id === activeCategoryId ? "" : cat.id); setCurrentPage(1); }}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategoryId === cat.id
                    ? "bg-orange-500 text-white shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-orange-600"
                }`}
              >
                <i className={`${cat.icon || 'ri-folder-line'} mr-1`} />
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* ── 工具栏：排序 ── */}
        <div className="flex items-center justify-end mb-6">
          {/* 排序 */}
          <div className="flex items-center gap-1.5 bg-white rounded-xl border border-gray-200 p-0.5">
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg transition-all cursor-pointer ${
                  sortBy === opt.key
                    ? "bg-[#8B4513] text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={opt.icon} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── 列表内容 ── */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
            <span className="ml-3 text-gray-500">加载中...</span>
          </div>
        ) : sortedListings.length > 0 ? (
          <div className="space-y-3 md:space-y-4">
            {sortedListings.map((listing, index) => (
              <ListingCard key={listing.id} listing={listing} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-orange-50">
              <i className="ri-search-line text-2xl text-orange-400" />
            </div>
            <p className="text-gray-500 font-medium">暂无相关信息</p>
            <p className="text-gray-400 text-sm mt-1">试试其他关键词或分类</p>
          </div>
        )}

        {/* ── 分页 ── */}
        {!loading && totalPages > 1 && (
          <div ref={titleRef} className={`mt-10 flex items-center justify-center gap-2 transition-all duration-500 ${titleVisible ? "opacity-100" : "opacity-0"}`}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-600 disabled:opacity-40 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" />
            </button>

            {[...Array(Math.min(7, totalPages))].map((_, i) => {
              let p: number;
              if (totalPages <= 7) p = i + 1;
              else if (currentPage <= 4) p = i + 1;
              else if (currentPage >= totalPages - 3) p = totalPages - 6 + i;
              else p = currentPage - 3 + i;

              return (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`w-10 h-10 flex items-center justify-center rounded-xl text-sm font-medium transition-all cursor-pointer ${
                    currentPage === p
                      ? "bg-[#8B4513] text-white shadow-sm"
                      : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600"
                  }`}
                >{p}</button>
              );
            })}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-600 disabled:opacity-40 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
